/**
 * Main App component for Budget 2025 Assistant
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Chat from './components/Chat';

// Loading component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
    <div className="text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <div className="mt-2 text-muted">
        Loading Budget 2025 Assistant...
      </div>
    </div>
  </div>
);

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mt-5">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Something went wrong
            </h4>
            <p>
              An unexpected error occurred. Please refresh the page or contact support if the problem persists.
            </p>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-3">
                <summary>Error Details (Development Mode)</summary>
                <pre className="mt-2 small">
                  {this.state.error && this.state.error.toString()}
                  <br />
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <hr />
            <button 
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              <i className="fas fa-redo me-2"></i>
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Root redirect component
const RootRedirect = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? <Navigate to="/chat" replace /> : <Navigate to="/login" replace />;
};

// Main app routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* Authentication */}
      <Route path="/login" element={<Login />} />
      
      {/* Protected routes */}
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin routes - placeholder for future implementation */}
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute requireAdmin={true}>
            <div className="container mt-5">
              <div className="alert alert-info">
                <h4>Admin Dashboard</h4>
                <p>Admin features coming soon...</p>
                <a href="/chat" className="btn btn-primary">
                  <i className="fas fa-comments me-2"></i>
                  Go to Chat
                </a>
              </div>
            </div>
          </ProtectedRoute>
        } 
      />

      {/* Search page - placeholder for future implementation */}
      <Route 
        path="/search" 
        element={
          <ProtectedRoute>
            <div className="container mt-5">
              <div className="alert alert-info">
                <h4>Document Search</h4>
                <p>Advanced search features coming soon...</p>
                <a href="/chat" className="btn btn-primary">
                  <i className="fas fa-comments me-2"></i>
                  Go to Chat
                </a>
              </div>
            </div>
          </ProtectedRoute>
        } 
      />
      
      {/* 404 page */}
      <Route 
        path="*" 
        element={
          <div className="container mt-5">
            <div className="alert alert-warning">
              <h4>
                <i className="fas fa-question-circle me-2"></i>
                Page Not Found
              </h4>
              <p>The page you're looking for doesn't exist.</p>
              <a href="/" className="btn btn-primary">
                <i className="fas fa-home me-2"></i>
                Go Home
              </a>
            </div>
          </div>
        } 
      />
    </Routes>
  );
};

// Main App component
const App = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;